import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { api } from '../../services/api';
import Button from '../ui/Button';
import { ErrorNotice } from '../ui/States';
import { fmt } from '../../lib/format';
import { errorMessage, inputClass } from '../../lib/programs';
import { stamp } from './externalFiles';
import type { EventDetail } from '../../lib/events';

interface SyncResult {
  dryRun: boolean;
  incremental: boolean;
  attendeesRead: number;
  ticketLines: number;
  people: number;
  newPeople: number;
  newContacts: number;
  updated: number;
  unchanged: number;
  declined: number;
  newTicketLines: number;
  changedTicketLines: number;
  duplicatesWithCore: number;
  restrictedSkipped: number;
  checkedInOnEventbrite: number;
  at: string;
  trigger: string;
  error?: string;
  errorAt?: string;
  added?: { members: number; newContacts: number; existingContacts: number };
  refunds?: number;
  inPerson?: number;
  virtual?: number;
  past?: boolean;
  ticketTypes: { fromEventbrite: number; capacitiesUpdated: number; notOnTheEvent: string[] } | null;
}
interface SyncState {
  configured: boolean;
  eventId: string | null;
  lastSyncAt: string | null;
  lastResult: SyncResult | null;
  automatic: boolean;
}
interface Candidate {
  id: string;
  name: string;
  start: string | null;
  status: string | null;
  organization: string | null;
}

function ResultLine({ r }: { r: SyncResult }) {
  return (
    <div className="mt-3 text-ink [&>p]:mt-1">
      <div className="text-small text-ink-3">
        {r.dryRun
          ? 'Check, nothing saved yet'
          : `${r.incremental ? 'Changes' : 'Full pull'} · ${r.trigger === 'schedule' ? 'automatic' : 'by hand'} · ${stamp(r.at)}`}
      </div>
      <p>
        {fmt(r.attendeesRead)} attendee record(s) read · {fmt(r.newPeople)} new on the list ({fmt(r.newContacts)} new
        contacts) · {fmt(r.updated)} updated · {fmt(r.unchanged)} unchanged · {fmt(r.declined)} declined (cancelled or
        refunded)
      </p>
      {r.added ? (
        <p>
          Added: {fmt(r.added.members)} member(s), {fmt(r.added.existingContacts)} known contact(s),{' '}
          {fmt(r.added.newContacts)} new contact(s) · ticket lines {fmt(r.newTicketLines)} new,{' '}
          {fmt(r.changedTicketLines)} changed · {fmt(r.inPerson ?? 0)} in person, {fmt(r.virtual ?? 0)} virtual ·{' '}
          {fmt(r.refunds ?? 0)} refunded or cancelled
        </p>
      ) : null}
      {r.past ? (
        <p className="text-ink-2">
          The event is over: statuses already on the list are kept (a refund only declines someone still registered);
          nobody is marked attended from Eventbrite.
        </p>
      ) : null}
      {r.duplicatesWithCore ? (
        <p>
          {fmt(r.duplicatesWithCore)} already on an invited audience or registered through the core form: check they did
          not pay twice.
        </p>
      ) : null}
      {r.checkedInOnEventbrite ? (
        <p className="text-ink-2">
          {fmt(r.checkedInOnEventbrite)} checked in on Eventbrite (check-ins come from PheedLoop&rsquo;s report, not
          from here).
        </p>
      ) : null}
      {r.ticketTypes?.notOnTheEvent.length ? (
        <p className="text-ink-2">
          Eventbrite ticket types not on Registration &amp; outreach: {r.ticketTypes.notOnTheEvent.join(', ')}.
        </p>
      ) : null}
      {r.error ? (
        <ErrorNotice>
          Last automatic pull failed {stamp(r.errorAt)}: {r.error}
        </ErrorNotice>
      ) : null}
    </div>
  );
}

export default function EventbriteSync({ eventId, canEdit }: { eventId: string; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [picking, setPicking] = useState(false);
  const state = useQuery<SyncState>(
    ['event-eventbrite-sync', eventId],
    async () => (await api.get(`/events/${eventId}/eventbrite/sync`)).data.data,
  );
  const event = useQuery<EventDetail>(['event', eventId], async () => (await api.get(`/events/${eventId}`)).data.data);
  const list = useQuery<Candidate[]>(
    ['event-eventbrite-candidates', eventId],
    async () => (await api.get(`/events/${eventId}/eventbrite/candidates`)).data.data,
    { enabled: picking },
  );
  const refresh = () => {
    queryClient.invalidateQueries('event-eventbrite-sync');
    queryClient.invalidateQueries('event-eventbrite');
    queryClient.invalidateQueries('event');
    queryClient.invalidateQueries('event-registration');
  };
  const link = useMutation(
    async (id: string | null) =>
      (await api.put(`/events/${eventId}/eventbrite/link`, { eventbriteEventId: id })).data.data,
    {
      onSuccess: () => {
        setEditing(false);
        setPicking(false);
        sync.reset();
        refresh();
      },
    },
  );
  const sync = useMutation(
    async (dryRun: boolean) =>
      (await api.post(`/events/${eventId}/eventbrite/sync`, { dryRun })).data.data as SyncResult,
    { onSuccess: refresh },
  );
  const s = state.data;
  if (!s) return null;
  const over = event.data ? new Date(event.data.event.endsAt) <= new Date() : false;
  if (!s.configured && !s.eventId && over) return null;
  const confirmed = Boolean(s.lastSyncAt);
  return (
    <div className="mb-6 pb-6 border-b border-line text-[15px] leading-[22px]">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <h3 className="font-semibold text-ink">Live sync</h3>
        <span className="text-ink-3">
          ·{' '}
          {!s.configured
            ? 'not available on this server'
            : s.automatic
              ? `every 15 minutes · last ${stamp(s.lastSyncAt)}`
              : s.eventId
                ? 'linked, not confirmed yet'
                : 'not linked'}
        </span>
      </div>
      {!s.configured ? (
        <p className="mt-1">
          Live sync needs an Eventbrite key on the server, and this server has none (a local copy of production never
          has one). Upload the export below instead.
        </p>
      ) : (
        <>
          <p className="mt-1">
            {s.eventId ? (
              <>
                Linked to Eventbrite event <span className="font-semibold">{s.eventId}</span>.{' '}
              </>
            ) : (
              'Not linked to an Eventbrite event yet. '
            )}
            {confirmed
              ? 'New and changed tickets arrive every 15 minutes; "Sync now" pulls them straight away.'
              : s.eventId
                ? 'Check the first pull, then confirm it; after that it runs every 15 minutes.'
                : ''}
          </p>
          {canEdit ? (
            <div className="flex flex-wrap gap-3 mt-3">
              {s.eventId && !confirmed ? (
                <Button size="sm" disabled={sync.isLoading} onClick={() => sync.mutate(true)}>
                  {sync.isLoading ? 'Reading Eventbrite…' : 'Check the first pull'}
                </Button>
              ) : null}
              {s.eventId ? (
                <Button
                  size="sm"
                  disabled={sync.isLoading || (!confirmed && !sync.data?.dryRun)}
                  onClick={() => sync.mutate(false)}
                >
                  {confirmed ? 'Sync now' : 'Confirm and turn on'}
                </Button>
              ) : null}
              <Button
                size="sm"
                variant="quiet"
                onClick={() => {
                  setEditing(!editing);
                  setValue(s.eventId || '');
                }}
              >
                {s.eventId ? 'Change the event' : 'Link an Eventbrite event'}
              </Button>
            </div>
          ) : null}
          {editing ? (
            <div className="mt-4">
              <label
                className="block text-[15px] leading-5 font-semibold text-ink mb-[6px]"
                htmlFor={`eb-id-${eventId}`}
              >
                Eventbrite event ID
              </label>
              <div className="flex flex-wrap gap-2 items-center">
                <input
                  id={`eb-id-${eventId}`}
                  className={`${inputClass} max-w-[360px]`}
                  value={value}
                  onChange={(e) => setValue(e.target.value.trim())}
                  placeholder="The number in its address"
                />
                <Button size="sm" disabled={link.isLoading} onClick={() => link.mutate(value || null)}>
                  Save
                </Button>
                {s.eventId ? (
                  <Button size="sm" variant="quiet" onClick={() => link.mutate(null)}>
                    Unlink
                  </Button>
                ) : null}
              </div>
              <Button size="sm" variant="quiet" className="mt-2" onClick={() => setPicking(true)}>
                Pick from Eventbrite
              </Button>
              {picking ? (
                list.isLoading ? (
                  <p className="mt-2 text-ink-2">Reading Eventbrite…</p>
                ) : list.error ? (
                  <ErrorNotice>{errorMessage(list.error, 'Could not list the Eventbrite events.')}</ErrorNotice>
                ) : (
                  <ul className="mt-2 max-h-[264px] overflow-auto border-t border-line-strong">
                    {(list.data ?? []).map((c) => (
                      <li key={c.id} className="flex gap-3 items-center border-b border-line py-1 min-h-[44px]">
                        <span className="flex-1">
                          {c.name}
                          <span className="block text-small text-ink-3">
                            {c.start ? c.start.slice(0, 10) : '–'} · {c.status} · {c.organization}
                          </span>
                        </span>
                        <Button size="sm" onClick={() => link.mutate(c.id)}>
                          Link
                        </Button>
                      </li>
                    ))}
                  </ul>
                )
              ) : null}
            </div>
          ) : null}
          {link.error || sync.error ? (
            <ErrorNotice>{errorMessage(link.error || sync.error, 'The Eventbrite sync failed.')}</ErrorNotice>
          ) : null}
          {sync.data ? <ResultLine r={sync.data} /> : s.lastResult ? <ResultLine r={s.lastResult} /> : null}
        </>
      )}
    </div>
  );
}
